import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from './apiClient';

// Fetch Hours by ICT ID
export const fetchHoursByICTId = createAsyncThunk(
  'hours/fetchHoursByICTId',
  async (ictId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/ictData/${ictId}/hours`);
      return { ictId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert New Hour
export const insertHour = createAsyncThunk(
  'hours/insertHour',
  async (hourData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/hours`, hourData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Hour
export const updateHour = createAsyncThunk(
  'hours/updateHour',
  async ({ id, TrainingHour, LabHour }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/hours/${id}`, {
        TrainingHour,
        LabHour,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Hour
export const deleteHour = createAsyncThunk(
  'hours/deleteHour',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/hours/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const hoursSlice = createSlice({
  name: 'hours',
  initialState: {
    hoursByIct: {},
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHoursByICTId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHoursByICTId.fulfilled, (state, action) => {
        state.loading = false;
        const { ictId, data } = action.payload;
        state.hoursByIct[ictId] = data;
      })
      .addCase(fetchHoursByICTId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertHour.fulfilled, (state, action) => {
        state.loading = false;
        const newHour = action.payload;
        const ictKey = newHour.ictId_id;
        if (!state.hoursByIct[ictKey]) {
          state.hoursByIct[ictKey] = [];
        }
        state.hoursByIct[ictKey].push(newHour);
      })
      .addCase(insertHour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHour.fulfilled, (state, action) => {
        state.loading = false;
        const updatedHour = action.payload;
        const ictKey = updatedHour.ictId_id;
        const index = state.hoursByIct[ictKey]?.findIndex(
          (hr) => hr.id === updatedHour.id
        );
        if (index !== -1) {
          state.hoursByIct[ictKey][index] = updatedHour;
        }
      })
      .addCase(updateHour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHour.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        for (const key in state.hoursByIct) {
          state.hoursByIct[key] = state.hoursByIct[key].filter(
            (hr) => hr.id !== deletedId
          );
        }
      })
      .addCase(deleteHour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default hoursSlice.reducer;
